import { computed, ref } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useSuperadminCliente } from '@/composables/useSuperadminCliente'
import {
  MODULO_RUTA_INICIO,
  MODULOS,
  esModuloValido,
  normalizarModulos,
  resolverModuloPorDefecto,
  type ModuloId,
} from '@/utils/modulos'

/**
 * Módulo VIGENTE en pantalla. Nunca se muestran dos módulos a la vez: postura y
 * engorde tienen formularios, indicadores y vocabulario distintos, y mezclarlos
 * es justo lo que se quiere evitar.
 *
 * - Cliente con un solo módulo: ese es el activo, y no hay nada que elegir.
 * - Cliente con los dos contratados: se conmuta desde el encabezado y la
 *   elección se recuerda por usuario y cliente.
 * - Superadmin: opera sobre el cliente que elige en el selector, así que sus
 *   módulos son los de ESE cliente, no "todos". Mientras la lista no carga se
 *   permite todo, para no esconder el menú y volverlo a mostrar.
 */

const STORAGE_KEY = 'aviar.modulo-activo.v1'

const leerSelecciones = (): Record<string, ModuloId> => {
  try {
    const crudo = localStorage.getItem(STORAGE_KEY)
    if (!crudo) return {}
    const parsed = JSON.parse(crudo)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

const seleccionesState = ref<Record<string, ModuloId>>(leerSelecciones())

export function useModulos() {
  const auth = useAuthStore()
  const { isSuperadmin, selectedCliente, selectedClienteId } = useSuperadminCliente()

  const clienteResuelto = computed(() => (isSuperadmin.value ? selectedCliente.value : null))

  /** true solo mientras el superadmin no tiene todavía un cliente resuelto. */
  const sinResolver = computed(() => isSuperadmin.value && !clienteResuelto.value)

  const modulos = computed<ModuloId[]>(() => {
    if (sinResolver.value) return [...MODULOS]
    if (isSuperadmin.value) return normalizarModulos(clienteResuelto.value?.modulos)
    return normalizarModulos(auth.user?.clienteModulos)
  })

  // Mientras no se sabe el cliente, `modulos` trae los dos por defecto: no es una
  // contratación doble, así que el conmutador no debe aparecer todavía.
  const tieneVariosModulos = computed(() => !sinResolver.value && modulos.value.length > 1)

  /** La elección se recuerda por usuario + cliente: dos clientes no la comparten. */
  const scopeKey = computed(() => {
    const userId = Number(auth.user?.id ?? 0)
    const clienteId = Number(
      (isSuperadmin.value ? selectedClienteId.value : auth.user?.clienteId) ?? 0,
    )
    if (!userId || !clienteId) return ''
    return `${userId}:${clienteId}`
  })

  const moduloPorDefecto = computed<ModuloId>(() =>
    resolverModuloPorDefecto(
      modulos.value,
      isSuperadmin.value
        ? clienteResuelto.value?.moduloPorDefecto
        : auth.user?.clienteModuloPorDefecto,
    ),
  )

  const moduloActivo = computed<ModuloId>(() => {
    const elegido = scopeKey.value ? seleccionesState.value[scopeKey.value] : undefined
    // Una elección vieja deja de valer si al cliente le quitaron ese módulo.
    if (esModuloValido(elegido) && modulos.value.includes(elegido as ModuloId)) {
      return elegido as ModuloId
    }
    return moduloPorDefecto.value
  })

  const setModuloActivo = (modulo: ModuloId): void => {
    if (!modulos.value.includes(modulo) || !scopeKey.value) return

    seleccionesState.value = { ...seleccionesState.value, [scopeKey.value]: modulo }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seleccionesState.value))
  }

  /** A dónde se manda al usuario al iniciar sesión o al rebotarlo de una ruta ajena. */
  const rutaInicio = computed<string>(() =>
    isSuperadmin.value ? '/admin/clientes' : MODULO_RUTA_INICIO[moduloActivo.value],
  )

  /**
   * Las rutas sin módulo (estructuras, inventario, admin…) son comunes y pasan
   * siempre. Las demás solo abren si son del módulo ACTIVO, no de cualquiera de
   * los contratados: con los dos habilitados se vería todo mezclado otra vez.
   */
  const puedeAccederModulo = (modulo: ModuloId | undefined | null): boolean => {
    if (!modulo) return true
    if (sinResolver.value) return true
    return modulo === moduloActivo.value
  }

  return {
    modulos,
    moduloActivo,
    /** true mientras el superadmin no tiene cliente resuelto. */
    sinResolver,
    moduloPorDefecto,
    tieneVariosModulos,
    setModuloActivo,
    rutaInicio,
    puedeAccederModulo,
  }
}
